import { Maximize2, RefreshCw } from "lucide-react";
import type { PaneSide } from "./types";
import { MaterialPicker } from "./MaterialPicker";
import { AlertBellToggle } from "./AlertBellToggle";
import { TooltipIconButton } from "@/components/trading/TooltipIconButton";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface PaneHeadProps {
  side: PaneSide;
  materialId: string;
  focused: boolean;
  refreshPending: boolean;
  refreshMessage: string | null;
  onMaterialChange: (materialId: string) => void;
  onRefresh: () => void;
  onToggleFullscreen: () => void;
  onFocusPane: () => void;
  searchFocusSignal: number;
}

export function PaneHead({
  side,
  materialId,
  focused,
  refreshPending,
  refreshMessage,
  onMaterialChange,
  onRefresh,
  onToggleFullscreen,
  onFocusPane,
  searchFocusSignal,
}: PaneHeadProps) {
  const sideLabel = side === "left" ? "Left" : "Right";

  return (
    <header
      className="flex items-center gap-2 min-w-0 min-h-[42px] px-2 py-1.5 border-b border-border relative"
      onFocus={onFocusPane}
    >
      <div className="flex items-center flex-shrink-0 gap-1.5 text-xs uppercase text-muted-foreground">
        <span>{sideLabel}</span>
        {focused ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs border-primary/45 text-primary cursor-help">
                Focused
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              This pane is keyboard-active. Press j/k to navigate, or click another pane to switch focus.
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <MaterialPicker
        ariaLabel={`${side} material picker`}
        autoFocusSearchSignal={searchFocusSignal}
        onChange={onMaterialChange}
        value={materialId}
      />
      <AlertBellToggle materialId={materialId} />
      <div className="flex flex-shrink-0 gap-1 ms-auto">
        <TooltipIconButton
          ariaPressed={refreshPending}
          label="Refresh feed (cooldown 30s after each refresh)"
          onClick={onRefresh}
          disabled={refreshPending}
        >
          <RefreshCw className={cn("h-4 w-4", refreshPending && "animate-spin")} />
        </TooltipIconButton>
        <TooltipIconButton label="Fullscreen this pane" onClick={onToggleFullscreen}>
          <Maximize2 className="h-4 w-4" />
        </TooltipIconButton>
      </div>
      {refreshMessage ? (
        <div className="absolute bottom-0 left-2 text-xs text-muted-foreground translate-y-full">
          {refreshMessage}
        </div>
      ) : null}
    </header>
  );
}
